import NiceModal, { useModal } from '@ebay/nice-modal-react';
import {
  Alert,
  Button,
  Drawer,
  Group,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconFile, IconPhoto, IconUpload, IconVideo, IconVolume } from '@tabler/icons-react';
import { useRef, useState } from 'react';

import { useEditContentMaterial, useReplaceContentMaterialFile } from '../api';
import { MEDIA_CONFIG } from '../constants/mediaConfig';
import { getEditableStatusOptions } from '../constants/status';
import { formatMaxMediaSizeLabel } from '../constants/uploadLimits';
import { useContentCategoryOptions } from '../hooks/useContentCategoryOptions';
import { contentTypes } from '../types';
import { formatFileSize } from '../utils/format';

import { validateMediaFile } from './BulkUpload/validateFile';

import { Platform } from '@/features/pages';

type MediaType = Exclude<contentTypes.ContentType, 'text'>;

type EditMaterialDrawerProps = {
  itemId: string;
  pageId?: string;
  platform: Platform;
  managerId?: string;
  initialValues: {
    type: MediaType;
    name: string;
    category: string;
    status: contentTypes.Status;
    filename: string;
    size: number;
  };
};

type Fields = EditMaterialDrawerProps['initialValues'];

const TYPE_LABELS: Record<MediaType, string> = {
  photo: 'photo',
  video: 'video',
  audio: 'audio',
  document: 'document',
};

const TYPE_ICONS: Record<MediaType, typeof IconPhoto> = {
  photo: IconPhoto,
  video: IconVideo,
  audio: IconVolume,
  document: IconFile,
};

export const EditMaterialDrawer = NiceModal.create(
  ({ itemId, pageId, platform, managerId, initialValues }: EditMaterialDrawerProps) => {
    const modal = useModal();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const editMaterial = useEditContentMaterial({ pageId });
    const replaceFile = useReplaceContentMaterialFile({ pageId });
    const categoryOptions = useContentCategoryOptions(pageId);
    const TypeIcon = TYPE_ICONS[initialValues.type];
    const mediaConfig = MEDIA_CONFIG[initialValues.type];

    const [fileError, setFileError] = useState<string | null>(null);

    const form = useForm<Fields>({
      initialValues,
      validate: {
        name: (value) => (!value.trim() ? 'Обязательное поле' : null),
      },
    });

    const save = (status: contentTypes.Status) => {
      const values = form.values;
      editMaterial.mutate(
        {
          id: itemId,
          managerId,
          name: values.name.trim(),
          category: values.category.trim(),
          status,
        },
        { onSuccess: () => modal.remove() }
      );
    };

    const handleFileChange = (file: File | null) => {
      if (!file) return;

      const validation = validateMediaFile(file, initialValues.type, platform);
      if (!validation.ok) {
        setFileError(validation.message);
        return;
      }

      setFileError(null);
      replaceFile.mutate(
        { id: itemId, file, managerId },
        {
          onSuccess: () => {
            form.setFieldValue('filename', file.name);
            form.setFieldValue('size', file.size);
          },
        }
      );
    };

    const isBusy = editMaterial.isLoading || replaceFile.isLoading;

    return (
      <Drawer
        opened={modal.visible}
        onClose={() => modal.remove()}
        position="right"
        size={420}
        title="Редактировать материал"
        overlayProps={{ opacity: 0.4, blur: 1.5 }}
        padding="md"
      >
        <Text size="sm" c="dimmed" mb="md">
          Тип: {TYPE_LABELS[initialValues.type]}
        </Text>

        <form onSubmit={form.onSubmit((values) => save(values.status))}>
          <Stack gap="md">
            <TextInput label="Название" withAsterisk {...form.getInputProps('name')} />
            <Select
              label="Категория"
              value={form.values.category}
              onChange={(value) => form.setFieldValue('category', value || '')}
              data={categoryOptions}
              searchable
              clearable
              nothingFoundMessage="Нет категории"
            />
            <div>
              <Text size="sm" fw={500} mb={6}>
                Статус
              </Text>
              <SegmentedControl
                data={getEditableStatusOptions(initialValues.status)}
                value={form.values.status}
                onChange={(value) => form.setFieldValue('status', value as contentTypes.Status)}
                fullWidth
              />
            </div>

            <div>
              <Group justify="space-between" mb={6}>
                <Text size="sm" fw={500}>
                  Файл
                </Text>
                <Button
                  variant="light"
                  size="xs"
                  leftSection={<IconUpload size={14} />}
                  loading={replaceFile.isLoading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Заменить файл
                </Button>
              </Group>
              <input
                ref={fileInputRef}
                type="file"
                accept={mediaConfig.accept}
                hidden
                onChange={(event) => {
                  handleFileChange(event.target.files?.[0] ?? null);
                  event.target.value = '';
                }}
              />
              <Paper
                p="sm"
                radius="md"
                withBorder
                style={{ borderColor: 'var(--mantine-color-dark-4)' }}
              >
                <Group wrap="nowrap" align="flex-start" gap="sm">
                  <ThemeIcon variant="light" size="lg" radius="md">
                    <TypeIcon size={18} />
                  </ThemeIcon>
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={500} lineClamp={1}>
                      {form.values.filename || '—'}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatFileSize(form.values.size)}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {mediaConfig.formatsHint} ·{' '}
                      {formatMaxMediaSizeLabel(platform, initialValues.type)}
                    </Text>
                  </Stack>
                </Group>
              </Paper>
              {fileError && (
                <Alert color="red" mt="xs" p="xs">
                  {fileError}
                </Alert>
              )}
            </div>

            <Group justify="flex-end" gap="sm" mt="lg">
              <Button variant="default" onClick={() => modal.remove()}>
                Отмена
              </Button>
              <Button type="submit" loading={isBusy}>
                Сохранить
              </Button>
            </Group>
          </Stack>
        </form>
      </Drawer>
    );
  }
);
